import React, { forwardRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import OlcDropdownChevron from '~/components/General/OlcDropdownChevron';

import './styles.scss';

const getPopperWidth = (popperWidth, referenceWidth) =>
  typeof popperWidth === 'number' ? `${popperWidth}px` : popperWidth || `${referenceWidth}px`;

const createWidthModifier = (popperWidth) => ({
  name: 'sameWidth',
  enabled: true,
  phase: 'beforeWrite',
  requires: ['computeStyles'],
  fn: ({ state }) => {
    const width = getPopperWidth(popperWidth, state.rects.reference.width);
    state.styles.popper.width = width;
  },
  effect: ({ state }) => {
    state.elements.popper.style.width = getPopperWidth(
      popperWidth,
      state.elements.reference.offsetWidth
    );

    return () => {
      state.elements.popper.style.width = '';
    };
  },
});

const joinClasses = (...classes) => classes.filter(Boolean).join(' ');

/**
 * Shared date picker used by all standard date and date-range fields.
 * It keeps the calendar aligned to the field width and uses the same chevron
 * as the application's single- and multi-select controls.
 */
const OlcDatePicker = forwardRef(
  (
    {
      wrapperClassName,
      popperClassName,
      calendarClassName,
      popperModifiers = [],
      popperWidth,
      icon,
      fixedHeight = true,
      hideOutsideMonthDays = true,
      onCalendarClose,
      onCalendarOpen,
      showIcon = true,
      showPopperArrow = false,
      toggleCalendarOnIconClick = true,
      ...props
    },
    ref
  ) => {
    const [calendarOpen, setCalendarOpen] = useState(false);
    const widthModifier = createWidthModifier(popperWidth);
    const sharedModifiers = [
      ...popperModifiers.filter((modifier) => modifier?.name !== widthModifier.name),
      widthModifier,
    ];

    return (
      <ReactDatePicker
        {...props}
        ref={ref}
        wrapperClassName={joinClasses('olcDatePicker', wrapperClassName)}
        popperClassName={joinClasses('olcDatePickerPopper', popperClassName)}
        calendarClassName={joinClasses(
          'olcDatePickerCalendar',
          hideOutsideMonthDays ? 'olcDatePickerCalendar--hideOutsideMonthDays' : '',
          calendarClassName
        )}
        popperModifiers={sharedModifiers}
        fixedHeight={fixedHeight}
        showIcon={showIcon}
        icon={icon ?? <OlcDropdownChevron open={calendarOpen} />}
        onCalendarOpen={() => {
          setCalendarOpen(true);
          onCalendarOpen?.();
        }}
        onCalendarClose={() => {
          setCalendarOpen(false);
          onCalendarClose?.();
        }}
        showPopperArrow={showPopperArrow}
        toggleCalendarOnIconClick={toggleCalendarOnIconClick}
      />
    );
  }
);

OlcDatePicker.displayName = 'OlcDatePicker';

export default OlcDatePicker;
